import * as TFS_Rest_Utils from "Presentation/Scripts/TFS/TFS.Rest.Utils";
import { WebApiTeam } from "TFS/Core/Contracts";
import * as CoreRestClient from "TFS/Core/RestClient";
import { TeamContext } from "VSS/Common/Contracts/Platform";
import { IComboOptions } from "VSS/Controls/Combos";
import * as VSS_Service from "VSS/Service";
import * as Utils_String from "VSS/Utils/String";
import * as Widgets_Resources from "Widgets/Scripts/Resources/TFS.Resources.Widgets";
import { TypedComboO } from "Widgets/Scripts/Shared/TypedCombo";

interface TeamPickerOptions extends IComboOptions {
    /**
     * The team to be automatically picked after the control is rendered. If not provided, the first team in the list is picked. If there are no teams in the list or the initial team selection does not exist, nothing is selected.
     */
    initialSelectionId?: string;
    /**
     * Callback that is executed after attempting to select the provided initial team selection and it does not exist.
     */
    onInitialSelectionNotFoundError?: (errorMessage: string) => void;
    projectId: string;
}

/**
 * A control whose data is sourced from the TFS operational store, not from analytics.
 */
export class TeamPicker extends TypedComboO<TeamContext, TeamPickerOptions> {
    public initializeOptions(options?: TeamPickerOptions): void {
        super.initializeOptions({
            allowEdit: true,
            autoComplete: true,
            // Prevents the combo dropdown from expanding past the parent control.
            fixDropWidth: true,
            placeholderText: Widgets_Resources.ConfigurationTeamPicker_Placeholder,
            ...options
        });
    }

    public initialize(): void {
        super.initialize();

        this.populate();
    }

    /**
     * Returns an error message if there is no valid team selected.
     */
    public getErrorMessage(): string | null {
        if (this.getValue() === null) {
            return Widgets_Resources.ConfigurationTeamPicker_InvalidTeamSelected;
        }

        return null;
    }

    private getTeams(): IPromise<WebApiTeam[]> {
        const restClient = VSS_Service.getClient(CoreRestClient.CoreHttpClient4);
        const getTeams = (top: number, skip: number) => restClient.getTeams(this._options.projectId, top, skip);

        return TFS_Rest_Utils.batchGet(getTeams, 500);
    }

    private populate(): void {
        this.showBusyOverlay();

        this.getTeams().then((teams: WebApiTeam[]) => {
            this.setEntries(teams);

            if (teams && teams.length) {
                this.setInitialSelection();
            }

            this.hideBusyOverlay();
        });
    }

    private setInitialSelection(): void {
        const { initialSelectionId } = this._options;

        if (initialSelectionId) {
            const teamExists = this.setSelectedByPredicate(team => team.id === initialSelectionId, false);

            // Handle the case of a team that was deleted
            if (!teamExists) {
                this._options.onInitialSelectionNotFoundError && this._options.onInitialSelectionNotFoundError(Widgets_Resources.ConfigurationTeamPicker_InitialSelectionNotFound);
            }
        } else {
            this.setSelectedIndex(0, false);
        }
    }

    private setEntries(teams: TeamContext[]): void {
        const teamEntries = teams.slice().sort((a: TeamContext, b: TeamContext) => {
            return Utils_String.localeIgnoreCaseComparer(a.name, b.name);
        });
        this.setSource(teamEntries, team => team.name);
    }

    public showBusyOverlay(): JQuery {
        // Do this so the overlay is rendered within the combo box
        // instead of taking the whole area outside
        return super.showBusyOverlay().appendTo(this.getElement());
    }
}
